/**
 * InstanceOverrideEditSlideout — Stage 3c-2 (P0-IDENT).
 *
 * Edit form for per-instance identity-plane overrides. Rotation policy
 * lives at this scope per design §3.3 — the Capital Markets "15 min
 * TTL" example in the mockup is really an instance-level override.
 *
 * Fields:
 *   - svidTtlSeconds         (overrides Org default for this instance)
 *   - rotationWindowFraction (fraction of TTL)
 *   - jwtSvidTtlSeconds      (separate from SVID TTL)
 *   - spireEndpoint          (DR-on-isolated-SPIRE use case)
 *   - federationOverrides    (sandbox-federation use case per decision §2)
 *
 * NO compliance column per decision §1.
 *
 * Reached from the (future) per-instance detail page. Currently the
 * component is built but has no parent page yet — wires up when
 * /identity/instances/:id lands.
 */
import React, { useEffect, useState } from 'react';
import { Save, X as XIcon, AlertTriangle, Trash2 } from 'lucide-react';
import { Slideout } from '../Slideout';
import { Button } from '../Button';
import { Pill } from '../Pill';
import {
  updateIdentityInstanceOverride,
  resetIdentityInstanceOverride,
} from '../../../services/api';

export function InstanceOverrideEditSlideout({
  isOpen,
  onClose,
  onSaved,
  instanceId,
  instanceName,
  mode = 'add',
}) {
  const [svidTtl, setSvidTtl] = useState('');
  const [rotation, setRotation] = useState('');
  const [jwtTtl, setJwtTtl] = useState('');
  const [spireEndpoint, setSpireEndpoint] = useState('');
  const [federationOverrides, setFederationOverrides] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) return;
    setSvidTtl(''); setRotation(''); setJwtTtl(''); setSpireEndpoint('');
    setFederationOverrides(''); setError(null);
  }, [isOpen]);

  const inputCls =
    'v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink text-[13px]';

  const handleAdd = async () => {
    setError(null);
    const body = {};
    if (svidTtl)  body.svidTtlSeconds = parseInt(svidTtl, 10);
    if (rotation) body.rotationWindowFraction = parseFloat(rotation);
    if (jwtTtl)   body.jwtSvidTtlSeconds = parseInt(jwtTtl, 10);
    if (spireEndpoint.trim()) body.spireEndpoint = spireEndpoint.trim();
    if (federationOverrides.trim()) {
      const uuids = federationOverrides.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
      const bad = uuids.find(u => !/^[0-9a-fA-F-]{36}$/.test(u));
      if (bad) { setError(`Not a valid UUID: ${bad}`); return; }
      body.federationOverrides = uuids;
    }

    if (body.svidTtlSeconds != null && body.svidTtlSeconds <= 0) {
      setError('SVID TTL must be positive.');
      return;
    }
    if (body.rotationWindowFraction != null
        && (body.rotationWindowFraction <= 0 || body.rotationWindowFraction >= 1)) {
      setError('Rotation window fraction must be strictly between 0 and 1.');
      return;
    }
    if (Object.keys(body).length === 0) {
      setError('Set at least one field to install an override.');
      return;
    }

    setSaving(true);
    const res = await updateIdentityInstanceOverride(instanceId, body);
    setSaving(false);
    if (res?.ok === false) {
      setError(res.error || 'Save failed.');
      return;
    }
    if (typeof onSaved === 'function') onSaved(res?.data);
    onClose();
  };

  const handleRemove = async () => {
    setError(null);
    setSaving(true);
    const res = await resetIdentityInstanceOverride(instanceId);
    setSaving(false);
    if (res?.ok === false) {
      setError(res.error || 'Delete failed.');
      return;
    }
    if (typeof onSaved === 'function') onSaved(null);
    onClose();
  };

  const isRemove = mode === 'remove';

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={520}
      ariaLabel={isRemove ? 'Remove instance override' : 'Install instance override'}
      header={
        <div className="flex items-center gap-2">
          <h2 className="text-[15px] font-semibold text-v2-ink m-0">
            {isRemove ? 'Remove instance override' : 'Install instance override'}
          </h2>
          <Pill tone={isRemove ? 'danger' : 'trust'} size="sm">
            {isRemove ? 'Destructive' : 'Override'}
          </Pill>
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-v2-border-subtle">
          <Button variant="ghost" size="sm" iconLeft={<XIcon size={13} />} onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          {isRemove ? (
            <Button variant="danger" size="sm" iconLeft={<Trash2 size={13} />}
                    onClick={handleRemove} disabled={saving}>
              {saving ? 'Removing…' : 'Remove override'}
            </Button>
          ) : (
            <Button variant="primary" size="sm" iconLeft={<Save size={13} />}
                    onClick={handleAdd} disabled={saving}>
              {saving ? 'Saving…' : 'Install override'}
            </Button>
          )}
        </div>
      }
    >
      <div className="px-4 py-4 flex flex-col gap-4">
        {error && (
          <div className="bg-v2-danger-soft border border-v2-danger/30 rounded-v2-md px-3 py-2 text-[12.5px] text-v2-danger flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <div className="text-[12.5px] text-v2-ink-secondary">
          Instance: <span className="text-v2-ink font-semibold">{instanceName || instanceId}</span>
        </div>

        {isRemove ? (
          <div className="bg-v2-warning-soft border border-v2-warning/30 rounded-v2-md px-3 py-2.5 text-[12.5px] text-v2-warning">
            Removing reverts the instance to inherit from tenant / org defaults.
            Active SVIDs continue under the new resolved policy at their next
            rotation cycle.
          </div>
        ) : (
          <>
            <div className="text-[11px] text-v2-ink-muted">
              Sparse update — leave a field blank to preserve existing value.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] text-v2-ink-secondary mb-1">SVID TTL (seconds)</label>
                <input type="number" min="1" value={svidTtl}
                       onChange={(e) => setSvidTtl(e.target.value)}
                       placeholder="900" className={inputCls} />
              </div>
              <div>
                <label className="block text-[11px] text-v2-ink-secondary mb-1">Rotation fraction</label>
                <input type="number" step="0.001" min="0.001" max="0.999"
                       value={rotation} onChange={(e) => setRotation(e.target.value)}
                       placeholder="0.667" className={inputCls} />
              </div>
              <div>
                <label className="block text-[11px] text-v2-ink-secondary mb-1">JWT-SVID TTL (seconds)</label>
                <input type="number" min="1" value={jwtTtl}
                       onChange={(e) => setJwtTtl(e.target.value)}
                       placeholder="300" className={inputCls} />
              </div>
              <div>
                <label className="block text-[11px] text-v2-ink-secondary mb-1">SPIRE endpoint</label>
                <input type="text" value={spireEndpoint}
                       onChange={(e) => setSpireEndpoint(e.target.value)}
                       placeholder="spire-dr.acmebank.io:8081" className={inputCls} />
              </div>
            </div>
            <div>
              <label className="block text-[11px] text-v2-ink-secondary mb-1">
                Federation override (peer UUIDs, comma-separated)
              </label>
              <input type="text" value={federationOverrides}
                     onChange={(e) => setFederationOverrides(e.target.value)}
                     placeholder="uuid-1, uuid-2" className={inputCls} />
              <div className="text-[10.5px] text-v2-ink-muted mt-1">
                Sandbox-federation pattern — Dev instance federates with partner
                sandbox; Prod instance federates with partner prod.
              </div>
            </div>
          </>
        )}
      </div>
    </Slideout>
  );
}

export default InstanceOverrideEditSlideout;
